import { ApolloError, DocumentNode, ErrorPolicy, FetchPolicy, LazyQueryResultTuple, OperationVariables, QueryHookOptions, TypedDocumentNode } from '@apollo/client';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { subscribe } from '../../config/cache';

export type TUseQuerAllProps<TQuery, TVariables extends OperationVariables, TSubscriptionVariables> = {
  fetchPolicy?: FetchPolicy;
  errorPolicy?: ErrorPolicy;
  notifyOnNetworkStatusChange?: boolean;
  WatchQueryFetchPolicy?: FetchPolicy;
  subscriptionDoc?: DocumentNode | TypedDocumentNode;
  subscriptionVariables?: TSubscriptionVariables;
  useQuery: (baseOptions: QueryHookOptions<TQuery, TVariables>) => LazyQueryResultTuple<TQuery, TVariables>;
  variables?: TVariables;
  isScroll?: boolean;
  skip?: boolean;
};

export const useQueryAll = <TQuery, TVariables extends OperationVariables, TSubscriptionVariables = undefined>(props: TUseQuerAllProps<TQuery, TVariables, TSubscriptionVariables>) => {
  const {
    //fetchPolicy = 'network-only',
    fetchPolicy = 'cache-first',
    errorPolicy = 'all',
    notifyOnNetworkStatusChange = true,
    subscriptionDoc,
    subscriptionVariables,
    useQuery,
    variables,
    isScroll = false,
    skip = false
  } = props;

  //const [loading, setLoading] = useState(true);

  const [fetchData, { data, error, loading, fetchMore, refetch }] = useQuery({
    variables,
    fetchPolicy,
    notifyOnNetworkStatusChange,
    errorPolicy,
    skip
  });

  const nextToken = useMemo(() => {
    if (data && typeof data === 'object') for (const value of Object.values(data)) if (value && typeof value === 'object' && value.nextToken) return value.nextToken;
    //return null;
  }, [data]);

  /* useEffect(() => {
    console.log('networkStatus:', networkStatus);
    }, [networkStatus]); */

  useEffect(() => {
    //console.log('useQueryAll():data', data);
    if (skip || data || error)
      //setLoading(false);
      return;

    fetchData();
  }, [data, error, fetchData, skip]);

  const fetchedNextTokenRef = useRef<string | undefined>(undefined);
  const variablesKey = useMemo(() => JSON.stringify({ skip, variables: variables || {} }), [skip, variables]);
  const variablesGenerationRef = useRef(0);
  const [fetchMoreError, setFetchMoreError] = useState<ApolloError>();

  const hasShownFirstPageToastRef = useRef(false);

  useLayoutEffect(() => {
    variablesGenerationRef.current += 1;
    fetchedNextTokenRef.current = undefined;
    setFetchMoreError(undefined);
    hasShownFirstPageToastRef.current = false;
  }, [variablesKey]);

  useEffect(() => {
    if (!isScroll || !data || !nextToken || hasShownFirstPageToastRef.current) return;
    if (!window.location.pathname.includes('.new')) return;

    hasShownFirstPageToastRef.current = true;
    toast.info('Showing the first 100 results. Apply a filter to narrow your search.');
  }, [data, isScroll, nextToken]);

  useEffect(() => {
    if (skip || loading || !nextToken || isScroll || nextToken === fetchedNextTokenRef.current) return;

    const requestedGeneration = variablesGenerationRef.current;
    fetchedNextTokenRef.current = nextToken;
    fetchMore({
      variables: {
        ...(variables || {}),
        nextToken
      },
      updateQuery: (prev: any, { fetchMoreResult }: { fetchMoreResult?: any }) => {
        if (variablesGenerationRef.current !== requestedGeneration) return prev;
        if (!fetchMoreResult) return prev;
        const merged: any = { ...prev };
        for (const key of Object.keys(fetchMoreResult)) {
          const prevValue = prev[key];
          const newValue = fetchMoreResult[key];
          if (prevValue?.items && newValue?.items) merged[key] = { ...newValue, items: [...prevValue.items, ...newValue.items] };
          else merged[key] = newValue;
        }
        return merged;
      }
    })
      .then((result) => {
        if (variablesGenerationRef.current !== requestedGeneration || !result.errors?.length) return;
        setFetchMoreError(new ApolloError({ graphQLErrors: result.errors }));
      })
      .catch((error: ApolloError) => {
        if (variablesGenerationRef.current !== requestedGeneration) return;
        setFetchMoreError(error);
      });
  }, [fetchMore, isScroll, loading, nextToken, skip, variables, variablesKey]);

  useEffect(() => {
    if (!subscriptionDoc || !subscriptionVariables) return;
    subscribe({
      query: subscriptionDoc,
      variables: subscriptionVariables
    });
  }, [subscriptionDoc, subscriptionVariables]);

  return {
    data,
    error: error || fetchMoreError,
    loading: !skip && loading,
    refetch,
    nextToken,
    fetchMore
  };
};
